import { useEffect, useMemo, useState } from 'react';
import {
  QuestionWrapper,
  useFormContext,
  useFormField,
  type FieldSchema,
  type Group,
  type QuestionOption,
} from '@stepform';

export interface DependantFieldSchema extends FieldSchema {
  dependsOn?: string;
  dependantOptions?: Record<string, QuestionOption[]>;
}

export function DropDownDependantField({
  fieldSchema,
  group,
}: {
  fieldSchema: DependantFieldSchema;
  group: Group;
}) {
  const { formService } = useFormContext();
  const field = useFormField(fieldSchema, group);
  const parentQuestion = fieldSchema.dependsOn
    ? formService.getQuestionById(fieldSchema.dependsOn)
    : null;
  const parentAnswer = parentQuestion?.answer ? String(parentQuestion.answer) : '';

  const options = useMemo<QuestionOption[]>(() => {
    if (!fieldSchema.dependantOptions) {
      return fieldSchema.options ?? [];
    }

    return fieldSchema.dependantOptions[parentAnswer] ?? [];
  }, [fieldSchema.dependantOptions, fieldSchema.options, parentAnswer]);

  const disabled = !parentAnswer;

  useEffect(() => {
    if (disabled && field.answer) {
      field.setAnswer('');
    }
  }, [disabled, field]);

  if (!field.show) {
    return null;
  }

  return (
    <QuestionWrapper {...field} fieldSchema={fieldSchema}>
      <div className="answer-group">
        <div className="answer select">
          <select
            className="text"
            value={String(field.answer ?? '')}
            disabled={disabled}
            onChange={(e) => field.setAnswer(e.target.value)}
            name={field.question.id}
            id={`input-${field.question.id}`}
          >
            <option value="">Please select</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label || option.value}
              </option>
            ))}
          </select>
        </div>
      </div>
    </QuestionWrapper>
  );
}

export function RadioDependantField({
  fieldSchema,
  group,
}: {
  fieldSchema: DependantFieldSchema;
  group: Group;
}) {
  const { formService } = useFormContext();
  const field = useFormField(fieldSchema, group);
  const parentQuestion = fieldSchema.dependsOn
    ? formService.getQuestionById(fieldSchema.dependsOn)
    : null;
  const parentAnswer = parentQuestion?.answer ? String(parentQuestion.answer) : '';

  const options = useMemo<QuestionOption[]>(() => {
    if (!fieldSchema.dependantOptions) {
      return fieldSchema.options ?? [];
    }

    return fieldSchema.dependantOptions[parentAnswer] ?? [];
  }, [fieldSchema.dependantOptions, fieldSchema.options, parentAnswer]);

  const disabled = !parentAnswer;

  useEffect(() => {
    if (disabled && field.answer) {
      field.setAnswer('');
    }
  }, [disabled, field]);

  if (!field.show) {
    return null;
  }

  return (
    <QuestionWrapper {...field} fieldSchema={fieldSchema}>
      <div className="answer-group big-buttons horizontal">
        <div className="inner">
          {options.map((option) => (
            <div className="answer" key={option.value}>
              <input
                type="radio"
                id={`${field.question.id}-${option.value}`}
                name={field.question.id}
                value={option.value}
                disabled={disabled}
                checked={field.answer === option.value}
                onChange={() => field.setAnswer(option.value)}
              />
              <label htmlFor={`${field.question.id}-${option.value}`}>
                {option.label || option.value}
              </label>
            </div>
          ))}
        </div>
      </div>
    </QuestionWrapper>
  );
}
